import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFlagUrl } from '../services/athleteDataService';

/**
 * Dynamic athlete card component with sleek design and transparent background
 * Showcases athletes with clean typography and interactive elements
 */
const AthleteCard = ({ 
  athlete, 
  variant = 'default',  // 'default', 'compact', 'featured', or 'premium'
  className = '' 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    // Animation delay on first load
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Card styles with transparent background and shape
  const cardStyles = {
    default: "relative overflow-hidden transition-all duration-500 rounded-xl",
    compact: "relative overflow-hidden transition-all duration-500 rounded-xl",
    featured: "relative overflow-hidden transition-all duration-500 rounded-xl",
    premium: "relative overflow-hidden transition-all duration-500 rounded-xl"
  };
  
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  if (!athlete) return null;

  // Render position badge with different color options
  const renderPositionBadge = (position) => {
    const positionColorMap = {
      'Portero': 'bg-yellow-500',
      'Defensa': 'bg-blue-500',
      'Mediocampista': 'bg-green-500',
      'Pivote': 'bg-green-500',
      'Delantero': 'bg-red-500',
      'Delantero centro': 'bg-red-500',
      'Extremo': 'bg-purple-500',
      'Medicentro': 'bg-emerald-500'
    };
    
    const basePosition = Object.keys(positionColorMap).find(p => 
      position.includes(p)
    ) || 'Delantero';
    
    const bgColor = positionColorMap[basePosition] || 'bg-red-500';
    
    // Use different style based on variant
    if (variant === 'default') {
      return (
        <div className={`${bgColor} text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-md`}>
          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse"></span>
          {position}
        </div>
      );
    } else {
      return (
        <div className={`${isHovered ? bgColor : 'bg-opacity-90 bg-black/70 backdrop-blur-sm'} text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md border border-white/20 transition-colors duration-300`}>
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-white animate-pulse mr-1"></span>
          {position}
        </div>
      );
    }
  };
  
  // Render enhanced flag display (circular with optional team badge)
  const renderFlags = () => {
    const flagSize = variant === 'default' ? "w-10 h-10" : "w-8 h-8";
    const flagSpacing = variant === 'default' ? "space-y-2" : "space-y-1.5";
    
    return (
      <div className={`flex flex-col items-center ${flagSpacing}`}>
        {athlete.countryCode && (
          <div className="relative group">
            <div className={`${flagSize} rounded-full border border-white/30 overflow-hidden shadow-md transition-transform duration-300 group-hover:scale-110`}>
              <img 
                src={getFlagUrl(athlete.countryCode)} 
                alt={`Bandera de ${athlete.country}`}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        )}
        {athlete.secondCountryCode && (
          <div className="relative group">
            <div className={`${flagSize} rounded-full border border-white/30 overflow-hidden shadow-md transition-transform duration-300 group-hover:scale-110`}>
              <img 
                src={getFlagUrl(athlete.secondCountryCode)}
                alt={`Segunda bandera`}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Modern dynamic card design with transparent background - Default variant
  if (variant === 'default') {
    return (
      <div
        className={`${cardStyles.default} ${className} transform transition-all duration-500 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} ${isHovered ? 'scale-105 shadow-lg' : 'scale-100'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ 
          transitionDelay: '0ms',
          background: 'transparent'
        }}
      >
        <Link to={`/atletas/${athlete.id}`} className="block">
          {/* Main content container */}
          <div className="relative aspect-[3/4]">
            {/* Player image with dynamic effects */}
            <img 
              src={athlete.image} 
              alt={athlete.name}
              className="w-full h-full object-cover transition-transform duration-500 rounded-xl"
              style={{
                transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                filter: 'contrast(1.1)'
              }}
            />
            
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 rounded-xl"></div>
            
            {/* Top overlay with number */}
            <div className="absolute top-2 right-2 z-20">
              <div className={`${athlete.number > 9 ? 'text-4xl' : 'text-5xl'} font-black text-white transition-transform duration-300`}
                   style={{ transform: isHovered ? 'translateY(-2px)' : 'translateY(0)' }}>
                {athlete.number}
              </div>
            </div>
            
            {/* Top left flags */}
            <div className="absolute top-2 left-2 z-20">
              {renderFlags()}
            </div>
            
            {/* Position badge - adjust position for dynamics */}
            <div className="absolute top-1/3 left-2 z-20 transition-transform duration-300"
                 style={{ transform: isHovered ? 'translateX(3px)' : 'translateX(0)' }}>
              {renderPositionBadge(athlete.specificPosition || athlete.position)}
            </div>
            
            {/* APY Group logo/watermark */}
            <div className="absolute bottom-24 left-0 right-0 flex justify-center z-20 opacity-70">
              <div className="flex items-center bg-black/30 backdrop-blur-sm px-2 py-1 rounded">
                <span className="text-white text-xs">AFY GROUP</span>
              </div>
            </div>
            
            {/* Player info overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
              {/* Player name */}
              <h3 className="text-2xl font-bold text-white tracking-tight leading-tight">
                {athlete.name}
              </h3>
              
              {/* Red underline */}
              <div className="h-0.5 w-12 bg-red-500 my-1.5"></div>
              
              {/* Stats section */}
              <div className="flex justify-between items-center mt-2">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white leading-none">{athlete.stats?.matches || 0}</div>
                  <div className="text-xs text-red-400 font-medium">Partidos</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white leading-none">{athlete.stats?.goals.toString().padStart(2, '0') || '00'}</div>
                  <div className="text-xs text-red-400 font-medium">Goles</div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }
  
  // Compact card variant - Shield shape design inspired by image 2
  if (variant === 'compact') {
    return (
      <div
        className={`${cardStyles.compact} ${className} group transform transition-all duration-500 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} hover:-translate-y-1 hover:shadow-lg`}
        style={{ 
          transitionDelay: '100ms',
          background: 'transparent'
        }}
      >
        <Link to={`/atletas/${athlete.id}`} className="block">
          {/* Content container with shield-like shape */}
          <div className="relative aspect-[3/4] overflow-hidden rounded-xl rounded-b-3xl">
            {/* Player image */}
            <img 
              src={athlete.image} 
              alt={athlete.name} 
              className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
              style={{ filter: 'contrast(1.05)' }}
            />
            
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-70"></div>
            
            {/* Position badge - using green color like in image 2 */}
            <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-20 transition-all duration-300">
              <div className="bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse"></span>
                {athlete.specificPosition || athlete.position}
              </div>
            </div>
            
            {/* Player number in circle - like image 2 */}
            <div className="absolute top-3 right-3 z-20">
              <div className="bg-red-500 text-white text-lg font-bold h-7 w-7 rounded-full flex items-center justify-center">
                {athlete.number}
              </div>
            </div>
            
            {/* Player name */}
            <div className="absolute top-1/3 left-0 right-0 text-center px-2 z-20">
              <h3 className="text-xl font-bold text-white leading-tight">
                {athlete.name}
              </h3>
              
              {/* Red underline */}
              <div className="h-0.5 w-12 bg-red-500 mx-auto my-1"></div>
            </div>
            
            {/* APY Group logo */}
            <div className="absolute bottom-14 left-0 right-0 flex justify-center z-10 opacity-80">
              <div className="text-center">
                <div className="text-xs text-gray-300 uppercase">AFY GROUP</div>
                <div className="text-xs text-gray-400">FOOTBALL MANAGEMENT</div>
              </div>
            </div>
            
            {/* Bottom flags */}
            <div className="absolute bottom-24 left-0 right-0 flex justify-center space-x-3 z-20">
              {athlete.countryCode && (
                <div className="relative">
                  <div className="w-6 h-6 rounded-full border border-white/20 overflow-hidden">
                    <img 
                      src={getFlagUrl(athlete.countryCode)} 
                      alt={`Bandera de ${athlete.country}`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              )}
              {athlete.secondCountryCode && (
                <div className="relative">
                  <div className="w-6 h-6 rounded-full border border-white/20 overflow-hidden">
                    <img 
                      src={getFlagUrl(athlete.secondCountryCode)}
                      alt={`Segunda bandera`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Stats section */}
            <div className="absolute bottom-3 left-0 right-0 px-3 z-20">
              <div className="flex justify-between items-center">
                <div className="text-center">
                  <div className="text-lg font-bold text-white leading-none">{athlete.stats?.matches || 0}</div>
                  <div className="text-xs text-red-400">Partidos</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-white leading-none">{athlete.stats?.goals.toString().padStart(2, '0') || '00'}</div>
                  <div className="text-xs text-red-400">Goles</div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }
  
  // Featured card variant inspired by image 1 but with more dynamic elements
  if (variant === 'featured') {
    return (
      <div 
        className={`${cardStyles.featured} ${className} transform transition-all duration-700 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'} hover:shadow-xl`}
        style={{ 
          transitionDelay: '200ms',
          background: 'transparent'
        }}
      >
        <Link to={`/atletas/${athlete.id}`} className="block">
          <div className="relative aspect-[4/5] overflow-hidden rounded-xl">
            {/* Player image with dynamic effects */}
            <img 
              src={athlete.image} 
              alt={athlete.name} 
              className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-105"
              style={{ filter: 'contrast(1.05)' }}
            />
            
            {/* Overlay with dark gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-75"></div>
            
            {/* Number in red circle - like image 1 */}
            <div className="absolute top-3 right-3 z-20">
              <div className="bg-red-500 text-white text-2xl font-bold h-10 w-10 rounded-full flex items-center justify-center shadow-md">
                {athlete.number}
              </div>
            </div>
            
            {/* Top left flags */}
            <div className="absolute top-3 left-3 flex flex-col space-y-2 z-20">
              {renderFlags()}
            </div>
            
            {/* Position badge - red like in image 1 */}
            <div className="absolute top-1/4 left-3 z-20">
              <div className="bg-red-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                {athlete.specificPosition || athlete.position}
              </div>
            </div>
            
            {/* Player name and stats */}
            <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
              {/* Player name */}
              <h2 className="text-3xl font-bold text-white tracking-tight leading-tight">
                {athlete.name}
              </h2>
              
              {/* Red underline */}
              <div className="h-0.5 w-16 bg-red-500 my-2"></div>
              
              {/* APY Group logo - subtle version */}
              <div className="mt-2 mb-4 opacity-70">
                <div className="flex items-center">
                  <div className="text-white text-xs">AFY GROUP</div>
                </div>
              </div>
              
              {/* Stats section */}
              <div className="flex justify-between items-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white leading-none">{athlete.stats?.matches || 0}</div>
                  <div className="text-xs text-red-400">Partidos</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white leading-none">{athlete.stats?.goals.toString().padStart(2, '0') || '00'}</div>
                  <div className="text-xs text-red-400">Goles</div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }
  
  // Premium variant - combining elements from both images with enhanced features
  if (variant === 'premium') {
    return (
      <div 
        className={`${cardStyles.premium} ${className} transform transition-all duration-700 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ 
          transitionDelay: '300ms',
          background: 'transparent'
        }}
      >
        <Link to={`/atletas/${athlete.id}`} className="block">
          <div className="relative aspect-[4/5] overflow-hidden rounded-xl">
            {/* Player image with subtle zoom effect */}
            <img 
              src={athlete.image} 
              alt={athlete.name} 
              className="w-full h-full object-cover transition-transform duration-700"
              style={{ 
                transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                filter: 'contrast(1.1)'
              }}
            />
            
            {/* Overlay with shield-like gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-80"></div>
            
            {/* Number in red circle - premium version */}
            <div className="absolute top-3 right-3 z-20 transition-transform duration-300"
                 style={{ transform: isHovered ? 'scale(1.1)' : 'scale(1)' }}>
              <div className="bg-red-500 text-white text-2xl font-bold h-10 w-10 rounded-full flex items-center justify-center shadow-md border border-white/20">
                {athlete.number}
              </div>
            </div>
            
            {/* Top left flags with hover effect */}
            <div className="absolute top-3 left-3 z-20">
              {renderFlags()}
            </div>
            
            {/* Position badge with dynamic color change */}
            <div className="absolute top-1/3 left-3 z-20 transition-all duration-300"
                 style={{ transform: isHovered ? 'translateX(5px)' : 'translateX(0)' }}>
              {renderPositionBadge(athlete.specificPosition || athlete.position)}
            </div>
            
            {/* Player name with elegant styling */}
            <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
              <h2 className="text-2xl font-bold text-white tracking-tight leading-tight transition-transform duration-300"
                  style={{ transform: isHovered ? 'translateY(-3px)' : 'translateY(0)' }}>
                {athlete.name}
              </h2>
              
              {/* Dynamic red underline */}
              <div className="h-0.5 bg-red-500 transition-all duration-300 my-2"
                   style={{ width: isHovered ? '70px' : '40px' }}></div>
              
              {/* APY Group logo with reveal effect */}
              <div className={`mt-2 mb-3 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-60'}`}>
                <div className="flex items-center">
                  <div className="text-white text-xs">AFY GROUP</div>
                </div>
              </div>
              
              {/* Stats section with hover effects */}
              <div className="flex justify-between items-center">
                <div className="text-center transition-transform duration-300"
                     style={{ transform: isHovered ? 'translateY(-3px)' : 'translateY(0)' }}>
                  <div className="text-2xl font-bold text-white leading-none">{athlete.stats?.matches || 0}</div>
                  <div className="text-xs text-red-400">Partidos</div>
                </div>
                <div className="text-center transition-transform duration-300"
                     style={{ transform: isHovered ? 'translateY(-3px)' : 'translateY(0)', transitionDelay: '50ms' }}>
                  <div className="text-2xl font-bold text-white leading-none">{athlete.stats?.goals.toString().padStart(2, '0') || '00'}</div>
                  <div className="text-xs text-red-400">Goles</div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }
  
  return null;
};

export default AthleteCard;